import type { FeedbackForm, FeedbackQuestion, QuestionType } from "./contract";
export const TYPE_LABELS: Record<QuestionType, string> = {
  short: "Short answer",
  paragraph: "Long answer",
  single: "Choose one",
  multiple: "Choose several",
  dropdown: "Dropdown",
  rating: "Star rating",
  scale: "Number scale",
  number: "Number",
  date: "Date",
  time: "Time",
  grid: "Question grid",
  section: "Section break",
};
export const newQuestion = (
  type: QuestionType = "short",
  title = "Untitled question",
): FeedbackQuestion => ({
  id: crypto.randomUUID(),
  type,
  title,
  description: "",
  required: false,
  options: ["Option 1", "Option 2"],
  rows: ["First item", "Second item"],
  min: type === "number" ? 0 : 1,
  max: type === "number" ? 100 : 5,
  condition: null,
});
export const TEMPLATES = [
  {
    id: "blank",
    title: "Start fresh",
    description: "A blank canvas for your next good question.",
    icon: "blank",
  },
  {
    id: "feedback",
    title: "Event feedback",
    description: "Find the highlights. Make the next event better.",
    icon: "event",
  },
  {
    id: "community",
    title: "Community pulse",
    description: "Discover what your players care about.",
    icon: "community",
  },
  {
    id: "interest",
    title: "Expression of interest",
    description: "Find the people who want to get involved.",
    icon: "interest",
  },
] as const;
export function template(id: string, eventId?: string): Partial<FeedbackForm> {
  let title = "Untitled form",
    description = "",
    questions = [newQuestion("short", "What would you like to share?")];
  if (id === "feedback") {
    title = "How was the adventure?";
    description =
      "The best events are shaped by the people who play them. Tell us what you loved and what we could do better.";
    questions = [
      {
        ...newQuestion("rating", "How was your overall experience?"),
        required: true,
      },
      {
        ...newQuestion("multiple", "What did you enjoy most?"),
        options: [
          "The challenge",
          "The community",
          "The rewards",
          "The organisation",
        ],
      },
      newQuestion("paragraph", "What would make the next event even better?"),
      {
        ...newQuestion("single", "Would you join another event like this?"),
        options: ["Absolutely", "Maybe", "Probably not"],
        required: true,
      },
    ];
  }
  if (id === "community") {
    title = "Help shape our community";
    description =
      "Your ideas help us choose what to build next. We would love to hear what matters to you.";
    questions = [
      {
        ...newQuestion("scale", "How connected do you feel to the community?"),
        min: 0,
        max: 10,
        required: true,
      },
      {
        ...newQuestion("multiple", "What would you like more of?"),
        options: [
          "Community events",
          "New modpacks",
          "Guides and tutorials",
          "Building projects",
          "Social activities",
        ],
      },
      newQuestion(
        "paragraph",
        "If you could change one thing, what would it be?",
      ),
    ];
  }
  if (id === "interest") {
    title = "Be part of what comes next";
    description =
      "Tell us how you would like to get involved. Our team will review your response.";
    questions = [
      {
        ...newQuestion("single", "How would you like to take part?"),
        options: ["As a player", "As a volunteer", "As a creator"],
        required: true,
      },
      newQuestion("paragraph", "What interests you about this?"),
      {
        ...newQuestion("multiple", "When are you usually available?"),
        options: [
          "Weekday mornings",
          "Weekday evenings",
          "Weekend mornings",
          "Weekend evenings",
        ],
      },
    ];
  }
  return {
    title,
    description,
    questions,
    confirmation:
      "Thanks for sharing your thoughts. Your response helps us make CDU better for everyone.",
    design: { accent: "#9fcda8", banner: "landscape", font: "sans" },
    status: "draft",
    closes_at: null,
    event_id: eventId || null,
    after_event: !!eventId,
    award_points: !!eventId,
  };
}
